import { Injectable } from '@angular/core'
import {
  checkAllTransactionsLinkedStatusAllEquals,
  checkAvailableActionsIncludes,
  checkSomeTransactionsLinkedStatusEquals,
  ClearwaterTransaction,
  ClearwaterTransactionsFilterContext,
  ExternalDataSourceType,
  ExternalTransaction,
  LinkedStatus,
} from '@helios/common-api'
import {
  BulkTransactionModalMode,
  BulkTransactionsModalService,
  CreateEditTransactionService,
  CreateTransactionsModalService,
  getTransactionTypeKeyFromCode,
  SecurityInfo,
  TransactionModalMode,
} from '@helios/create-transactions-modal'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { FilterModel } from '@helios/filter-modal'
import {
  ExternalFileFilter,
  ExternalFileLinkedPanel,
  RootState,
  setCommandsMetrics,
  setMatchedExtTransFilter,
  showRawDataAction,
} from '@helios/state-management'
import {
  ModalPositionService,
  UserPreferencesService,
} from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi } from '@northfork/ag-grid'
import { ToastService } from '@northfork/ng-basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { EditTransactionNoteModalComponent } from 'libs/generic-components/src/lib/edit-transaction-note-modal/edit-transaction-note-modal.component'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { GenericDeleteConfirmationModalComponent } from 'libs/generic-components/src/lib/generic-delete-confirmation-modal/generic-delete-confirmation-modal.component'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { TransactionDeleteModalComponent } from 'libs/generic-components/src/lib/transaction-delete-modal/transaction-delete-modal.component'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { TransactionDeleteNoteModalComponent } from 'libs/generic-components/src/lib/transaction-delete-note-modal/transaction-delete-note-modal.component'
import { forkJoin } from 'rxjs'
import { EditTransactionTypeLabelComponent } from '../components/edit-transaction-type-label/edit-transaction-type-label.component'
import { MoveToDifferentSleeveModalComponent } from '../components/move-to-different-sleeve-modal/move-to-different-sleeve-modal.component'
import { SpecLotModalService } from '../components/spec-lot-modal/spec-lot-modal.service'
import { UnmatchTransactionModalComponent } from '../components/transaction-modals'
import { TransactionsActionResponse } from '../models/clearwater-transactions-action.model'
import { CwTxSummaryResponse } from '../models/cw-tx-summary.model'
import { TransactionTypeLabelRequest } from '../models/transaction-type-labels.model'
import { isLinked } from '../utils/link-status-renderer'
import { ClearwaterTransactionsService } from './clearwater-transactions.service'
import { EditTransactionTypeLabelService } from './edit-transaction-type-label.service'

@Injectable({
  providedIn: 'root',
})
export class ClearwaterTransactionsTaskService {
  constructor(
    private readonly createEditService: CreateEditTransactionService,
    private readonly createTransactionsModalService: CreateTransactionsModalService,
    private readonly bulkTransactionsModalService: BulkTransactionsModalService,
    private readonly modalService: NgbModal,
    private readonly store: Store<RootState>,
    private readonly transactionsService: ClearwaterTransactionsService,
    private readonly specLotModalService: SpecLotModalService,
    readonly editTransactionTypeLabelService: EditTransactionTypeLabelService,
    public toastService: ToastService,
    readonly modalPositionService: ModalPositionService,
  ) {}
  bulkEditTrans(
    cwTranId: string[],
    selectedTranCode: string[],
    securityId: number[],
    securityData: SecurityInfo[],
    isTwoSidedTrno: boolean,
  ) {
    const tranType = getTransactionTypeKeyFromCode(selectedTranCode[0])
    this.bulkTransactionsModalService.toggleBulkTransactionModal(tranType, {
      modalMode: BulkTransactionModalMode.EditCWTran,
      cwTranId: cwTranId,
      securityId: securityId,
      securityData: securityData,
      isTwoSidedTrno: isTwoSidedTrno,
    })
  }

  findMatchedTransaction(transactions: ClearwaterTransaction[]) {
    this.store.dispatch(setMatchedExtTransFilter(transactions))
  }
  async selectAllTransactions(
    filters: FilterModel,
    context: ClearwaterTransactionsFilterContext,
  ) {
    return await this.transactionsService.getTransactions({
      context: context,
      startRow: 0,
      endRow: 100000,
      filter: filters,
      group: {
        keys: [],
        fields: [],
      },
      sort: [],
      fields: [
        'rowId',
        'tranId',
        'accountId',
        'tranTypeCode',
        'secCusip',
        'secId',
        'settleDate',
      ],
    })
  }

  async getAllTransactionsSummary(
    filters: FilterModel,
    filterContext: ClearwaterTransactionsFilterContext,
  ): Promise<CwTxSummaryResponse> {
    return await this.transactionsService.getAllTxsSummary({
      context: {
        ...filterContext,
      },
      startRow: 0,
      endRow: 0,
      filter: filters,
      group: {
        keys: [],
        fields: [],
      },
      sort: [],
      fields: [],
    })
  }

  clear(cwTrans: ClearwaterTransaction[]): Promise<TransactionsActionResponse> {
    const cwTransValidToClear: ClearwaterTransaction[] = []
    this.store.dispatch(
      setCommandsMetrics({
        command: 'clear',
        time: Date.now(),
      }),
    )
    cwTrans.forEach(tx => {
      if (tx.availableActions?.includes('clear')) {
        cwTransValidToClear.push(tx)
      }
    })
    return this.transactionsService.clear(cwTransValidToClear).toPromise()
  }

  clearAndSetPostDate(
    cwTrans: ClearwaterTransaction[],
  ): Promise<TransactionsActionResponse> {
    this.store.dispatch(
      setCommandsMetrics({
        command: 'clearAndSetPostDate',
        time: Date.now(),
      }),
    )
    return this.transactionsService.clearAndSetPostDate(cwTrans).toPromise()
  }

  unclear(
    cwTrans: ClearwaterTransaction[],
  ): Promise<TransactionsActionResponse> {
    const cwTransValidToUnclear: ClearwaterTransaction[] = []
    this.store.dispatch(
      setCommandsMetrics({
        command: 'unclear',
        time: Date.now(),
      }),
    )
    cwTrans.forEach(tx => {
      if (tx.availableActions?.includes('unclear')) {
        cwTransValidToUnclear.push(tx)
      }
    })
    return this.transactionsService.unclear(cwTransValidToUnclear).toPromise()
  }

  /**
   * Mark that Transaction as "deleted"
   */
  async delete(
    cwTrans: ClearwaterTransaction[],
  ): Promise<TransactionsActionResponse | undefined> {
    try {
      if (!UserPreferencesService.getHideDeletePromptPreference()) {
        await this.openDeleteModal(cwTrans)
      }
    } catch (err) {
      /* modal cancelled, nothing to do */
      return
    }

    try {
      return await this.transactionsService.delete(cwTrans).toPromise()
    } catch (err) {
      this.toastService.warning(
        err?.error?.[0]?.validationId === 'windowDate'
          ? 'Transaction is outside of the session window date. Please extend the session window to edit this transaction.'
          : 'Unable to delete transaction. Please try again.',
      )
      return
    }
  }

  async deleteAndIgnore(
    cwTrans: ClearwaterTransaction[],
  ): Promise<TransactionsActionResponse | undefined> {
    const cwTransValidToDeleteAndIgnore: ClearwaterTransaction[] = []
    cwTrans.forEach(tx => {
      if (
        tx.availableActions &&
        tx.availableActions.length > 0 &&
        (tx.availableActions?.includes('deleteAndIgnore') ||
          cwTrans.some(
            t =>
              t.linkedStatus == LinkedStatus.MATCHED ||
              t.linkedStatus == LinkedStatus.CUSTODY_MATCHED ||
              t.linkedStatus == LinkedStatus.TRADING_MATCHED,
          ))
      ) {
        cwTransValidToDeleteAndIgnore.push(tx)
      }
    })
    try {
      if (!UserPreferencesService.getHideDeletePromptPreference()) {
        await this.openDeleteModal(cwTransValidToDeleteAndIgnore)
      }
    } catch (err) {
      /* modal cancelled, nothing to do */
      return
    }
    this.store.dispatch(
      setCommandsMetrics({
        command: 'match',
        time: Date.now(),
      }),
    )
    return this.transactionsService
      .deleteAndIgnore(cwTransValidToDeleteAndIgnore)
      .toPromise()
  }

  /**
   * Match action handler.
   */
  async match(
    cwTrans: ClearwaterTransaction[],
    extTrans: ExternalTransaction[],
  ): Promise<TransactionsActionResponse | undefined> {
    if (cwTrans.length === 0 || extTrans.length === 0) {
      return
    }
    this.store.dispatch(
      setCommandsMetrics({
        command: 'match',
        time: Date.now(),
      }),
    )
    return this.transactionsService.match(cwTrans, extTrans).toPromise()
  }

  /**
   * Match action handler.
   */
  async manyToManyMatch(
    cwTrans: ClearwaterTransaction[],
    extTrans: ExternalTransaction[],
  ): Promise<TransactionsActionResponse | undefined> {
    if (cwTrans.length === 0 || extTrans.length === 0) {
      return
    }

    this.store.dispatch(
      setCommandsMetrics({
        command: 'manyToManyMatch',
        time: Date.now(),
      }),
    )

    return this.transactionsService
      .manyToManyMatch(cwTrans, extTrans)
      .toPromise()
  }

  /**
   * Match the Clearwater Transaction with the selected External-Transaction
   */
  async matchUsingExternal(
    cwTrans: ClearwaterTransaction[],
    extTrans: ExternalTransaction[],
  ) {
    const extTran: ExternalTransaction = extTrans[0]
    this.store.dispatch(
      setCommandsMetrics({
        command: 'matchUsingExternal',
        time: Date.now(),
      }),
    )
    await this.transactionsService
      .matchUsingExternal(cwTrans, extTran)
      .toPromise()
  }

  /**
   * Unmatch the Transaction from the External-Transaction (if there are
   * multiple matches, a dialog is presented to select which)
   */
  async unmatch(
    cwTrans: ClearwaterTransaction[],
    isEnfusionAccount?: boolean,
  ): Promise<TransactionsActionResponse | undefined> {
    if (cwTrans.length === 0) {
      return
    }

    this.store.dispatch(
      setCommandsMetrics({
        command: 'unmatch',
        time: Date.now(),
      }),
    )
    // If all transactions are unmatched, then simply return
    const areAllUnmatched = checkAllTransactionsLinkedStatusAllEquals(
      cwTrans,
      LinkedStatus.UNMATCHED,
    )
    if (areAllUnmatched) {
      return
    }
    const cwTransValidToUnmatch: ClearwaterTransaction[] = []
    cwTrans.forEach(tx => {
      if (!isLinked(tx) && checkAvailableActionsIncludes(tx, 'unmatch')) {
        cwTransValidToUnmatch.push(tx)
      }
    })

    // check and see if some of the transactions are MATCH
    const areSomeMatched = checkSomeTransactionsLinkedStatusEquals(
      cwTrans,
      LinkedStatus.MATCHED,
    )

    // If no transactions have linked status MATCHED, then simply unmatch them
    if (!areSomeMatched) {
      return this.transactionsService.unmatch(cwTransValidToUnmatch).toPromise()
    }

    // Show modal with Enfusion account restrictions if applicable
    let result: ExternalDataSourceType
    try {
      const modalRef = this.modalService.open(
        UnmatchTransactionModalComponent,
        {
          keyboard: false,
        },
      )
      modalRef.componentInstance.isEnfusionAccount = isEnfusionAccount
      result = (await modalRef.result)?.toUpperCase() as ExternalDataSourceType
    } catch (e) {
      /* modal cancelled, nothing to do */
      return
    }

    return this.transactionsService
      .unmatch(cwTransValidToUnmatch, result)
      .toPromise()
  }

  findTransInExternalFile(transaction: ClearwaterTransaction) {
    this.store.dispatch(
      showRawDataAction({
        id: transaction.tranId,
        reconDate: transaction.extReconDate,
        filterText: transaction.extCusip,
        accountId: transaction.accountId,
        panelName: ExternalFileLinkedPanel.CW_TRANSACTION,
      } as ExternalFileFilter),
    )
  }

  /**
   * Initiate the appropriate dialog for editing the category of Transaction
   * (Trade, Transfer, Cash-Entry, etc.)
   */
  editClearwaterTransaction(cwTran: ClearwaterTransaction) {
    this.createTransactionsModalService.toggleCreateTransactionModal(
      cwTran.tranTypeCode,
      {
        modalMode: TransactionModalMode.EditCWTran,
        transactionId: cwTran.tranId,
        origTransactionId: cwTran.legacyOrigId,
        securityId: cwTran.secId,
        transType: cwTran.tranTypeCode,
        transactionBasis: cwTran.basis,
      },
    )
  }

  /**
   * Open the transaction dialog in read-only (view) mode.
   * Used when the user does not have editTran permission.
   */
  viewClearwaterTransaction(cwTran: ClearwaterTransaction) {
    this.createTransactionsModalService.toggleCreateTransactionModal(
      cwTran.tranTypeCode,
      {
        modalMode: TransactionModalMode.EditCWTran,
        readOnly: true,
        transactionId: cwTran.tranId,
        origTransactionId: cwTran.legacyOrigId,
        securityId: cwTran.secId,
        transType: cwTran.tranTypeCode,
        transactionBasis: cwTran.basis,
      },
    )
  }

  editSpecLots(cwTran: ClearwaterTransaction, agGridApi: AgGridApi): void {
    if (cwTran.secId == undefined || cwTran.legacyOrigId == undefined) return
    this.specLotModalService.toggleSpecLotModal(
      cwTran.accountId,
      cwTran.secId,
      cwTran.tranId,
      cwTran.legacyOrigId,
      agGridApi,
    )
  }

  /**
   * Initiate the Edit Transaction Note dialog
   */
  async editNotes(cwTrans: ClearwaterTransaction[]): Promise<void[] | void> {
    if (cwTrans.length === 0) {
      return
    }

    const compKey = 'edit-transaction-note'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(EditTransactionNoteModalComponent, {
      windowClass: uniqueId,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    })
    const editModal =
      modalRef.componentInstance as EditTransactionNoteModalComponent
    const noteText = cwTrans.length === 1 ? cwTrans[0].tranNote ?? '' : ''
    editModal.noteText = noteText

    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)

    let newNoteText!: string
    try {
      newNoteText = await modalRef.result
    } catch (e) {
      /*modal cancelled, nothing to do*/
      return
    }

    return this.saveNoteChanges(cwTrans, newNoteText)
  }

  /**
   * Delete note for selected transactions
   */
  async deleteNotes(cwTrans: ClearwaterTransaction[]): Promise<void[] | void> {
    if (!cwTrans.length) return

    try {
      await this.openDeleteNoteModal()
    } catch (err) {
      /* modal cancelled, nothing to do */
      return
    }
    this.store.dispatch(
      setCommandsMetrics({
        command: 'deleteNotes',
        time: Date.now(),
      }),
    )

    return this.deleteNoteChanges(cwTrans)
  }

  async editTransactionTypeLabel(
    cwTrans: ClearwaterTransaction[],
  ): Promise<void[] | void> {
    if (cwTrans.length === 0) {
      return
    }
    const tranIds: string[] = []
    cwTrans.forEach(cwTran => {
      tranIds.push(cwTran.tranId)
    })

    const compKey = 'edit-transaction-label'
    const uniqueId = compKey + '-' + Date.now()

    const modalRef = this.modalService.open(EditTransactionTypeLabelComponent, {
      size: 'md',
      windowClass: `modal-window-click-through ${uniqueId}`,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)

    modalRef.componentInstance.transactionIds = tranIds
    if (cwTrans.length === 1) {
      modalRef.componentInstance.transactionLabel = cwTrans[0].typeLabel
    }

    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)

    return
  }

  /**
   * remove Transaction TypeLabel for selected transactions
   */
  async removeTransactionTypeLabel(cwTrans: ClearwaterTransaction[]) {
    if (!cwTrans.length) return

    try {
      await this.openRemoveTypeLabelModal()
    } catch (err) {
      /* modal cancelled, nothing to do */
      return
    }

    this.removeTypeLabel(cwTrans)
  }

  moveToDifferentSleeve(cwTrans: ClearwaterTransaction[]) {
    if (cwTrans.length === 0) {
      return
    }

    const compKey = 'move-to-different-sleeve'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(
      MoveToDifferentSleeveModalComponent,
      {
        size: 'lg',
        windowClass: `modal-window-click-through ${uniqueId}`,
        keyboard: false,
        beforeDismiss: () => {
          return this.modalPositionService.handleModalPositionSaveOnDismiss(
            uniqueId,
            compKey,
          )
        },
      } as NgbModalOptions,
    )
    modalRef.componentInstance.input = cwTrans
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  /**
   * Save just the note for a single transaction - from inline edit
   */
  saveNote(cwTran: ClearwaterTransaction): Promise<void[]> {
    const note = cwTran.tranNote ?? ''
    return this.saveNoteChanges([cwTran], note)
  }

  /**
   * Save a note for a multiple transactions
   */
  private saveNoteChanges(
    cwTrans: ClearwaterTransaction[],
    newValue: string,
  ): Promise<void[]> {
    this.store.dispatch(
      setCommandsMetrics({
        command: 'saveNoteChanges',
        time: Date.now(),
      }),
    )
    const editNoteCalls = cwTrans.map(tx => {
      const oldValue = tx.tranNote ?? ''
      return this.createEditService.editNote(tx.tranId, { oldValue, newValue })
    })

    return forkJoin(editNoteCalls).toPromise()
  }

  /**
   * Delete notes for a multiple transactions
   */
  private deleteNoteChanges(cwTrans: ClearwaterTransaction[]): Promise<void[]> {
    const deleteNoteRequests = cwTrans
      .filter(tran => tran.tranNote)
      .map(tx =>
        this.createEditService.editNote(
          tx.tranId,
          { oldValue: tx.tranNote ?? '', newValue: '' },
          true,
        ),
      )
    return forkJoin(deleteNoteRequests).toPromise()
  }

  private async openDeleteModal(
    cwTrans: ClearwaterTransaction[],
  ): Promise<void> {
    const modal = this.modalService.open(TransactionDeleteModalComponent, {
      keyboard: false,
    })
    const deleteModal: TransactionDeleteModalComponent = modal.componentInstance
    deleteModal.toDelete = cwTrans.map(tx => tx.tranId)
    return modal.result
  }

  private async openDeleteNoteModal(): Promise<void> {
    return this.modalService.open(TransactionDeleteNoteModalComponent, {
      keyboard: false,
    }).result
  }

  private async openRemoveTypeLabelModal(): Promise<void> {
    const modal = this.modalService.open(
      GenericDeleteConfirmationModalComponent,
      { keyboard: false },
    )
    const deleteModal: GenericDeleteConfirmationModalComponent =
      modal.componentInstance
    deleteModal.deleteConfirmationText =
      'Are you sure that you want to remove type label(s) for the selected transaction(s)?'
    deleteModal.deleteButtonText = 'Delete Type Label(s)'
    return modal.result
  }

  /**
   * removeTypeLabel for multiple transactions
   */
  private removeTypeLabel(cwTrans: ClearwaterTransaction[]) {
    const requestBody = {
      transactionIds: cwTrans.map(tran => tran.tranId),
    } as TransactionTypeLabelRequest
    const removeTypelabel = this.editTransactionTypeLabelService
      .saveTransactionLabel(requestBody)
      .subscribe(
        () => {
          this.toastService.success(
            'Remove transaction type label command is successfully submitted.',
          )
        },
        () => {
          this.toastService.error(
            'Remove transaction type label command submission failed.',
          )
        },
      )
  }
}
